/**
 * Catálogo de sobres a la venta.
 *
 * Vive en la base y no en el código para que cambiar un precio o sacar un sobre
 * de temporada no exija desplegar la aplicación. Se cachea largo: cambia como
 * mucho una vez por temporada.
 */

import { useQuery } from '@tanstack/react-query';
import { describeDatabaseError, supabase } from '@/services/supabaseClient';
import type { DbPackType } from '@/types/database';

export interface PackType {
  id: string;
  name: string;
  description: string;
  priceGold: number | null;
  priceGems: number | null;
  cardCount: number;
  /** Pesos relativos por rareza; se normalizan al mostrarlos como porcentaje. */
  rarityWeights: Record<string, number>;
}

function toPackType(row: DbPackType): PackType {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    priceGold: row.price_gold,
    priceGems: row.price_gems,
    cardCount: row.card_count,
    rarityWeights: row.rarity_weights,
  };
}

export function usePackTypes() {
  return useQuery({
    queryKey: ['packTypes'] as const,
    enabled: Boolean(supabase),
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<PackType[]> => {
      const { data, error } = await supabase!.from('pack_types').select('*').order('price_gold');
      if (error) throw new Error(describeDatabaseError(error));
      return data.map(toPackType);
    },
  });
}
